import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { API } from "@/App";
import axios from "axios";
import { gerarFL01PDF } from "@/lib/fl01PDF";
import { BookOpen, Printer } from "lucide-react";

const fmtData = (s) => (s ? new Date(String(s).slice(0, 10) + "T00:00:00").toLocaleDateString("pt-BR") : "—");

export default function CadernoObraPage() {
  const [obras, setObras] = useState([]);
  const [contratoId, setContratoId] = useState("");
  const [caderno, setCaderno] = useState(null);

  useEffect(() => { axios.get(`${API}/logistica/caderno-obras`).then(r => setObras(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!contratoId) { setCaderno(null); return; }
    axios.get(`${API}/logistica/caderno/${contratoId}`).then(r => setCaderno(r.data)).catch(() => {});
  }, [contratoId]);

  const folhas = caderno ? [...new Set(caderno.linhas.map(l => l.folha))].sort((a, b) => a - b) : [];

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="caderno-obra-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-luvcorp-navy flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-luvcorp-gold" /> Caderno da Obra (FL-01)
            </h1>
            <p className="text-sm text-slate-500">Folhas numeradas por obra (24 linhas/folha). Cada entrega, devolução ou ocorrência gera uma linha automaticamente.</p>
          </div>
          {caderno && caderno.linhas.length > 0 && (
            <Button onClick={() => gerarFL01PDF({ obraTitulo: caderno.obra_titulo, linhas: caderno.linhas, linhasPorFolha: caderno.linhas_por_folha })}
              className="bg-luvcorp-navy text-white" data-testid="btn-pdf-fl01">
              <Printer className="h-4 w-4 mr-1" /> Imprimir FL-01
            </Button>)}
        </div>

        <NativeSelect value={contratoId} onValueChange={setContratoId} placeholder="Selecione a obra…" className="max-w-xl" searchable
          options={obras.map(o => ({ value: o.contrato_id, label: `${o.obra_titulo} — ${o.linhas} linha(s), ${o.folhas} folha(s)` }))}
          data-testid="select-obra-caderno" />

        {!caderno ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-400 text-sm">
            {obras.length === 0 ? "Nenhum caderno ainda — as linhas são geradas pelas conferências dos romaneios." : "Selecione uma obra para ver o caderno."}
          </CardContent></Card>
        ) : folhas.map(f => (
          <Card key={f} className="border-0 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <p className="px-4 py-2 text-sm font-semibold text-luvcorp-navy bg-slate-50 border-b">Folha {f}</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-luvcorp-navy text-white text-left">
                    {["Nº", "Data", "Tipo", "Nº req./rom.", "Item", "Unid.", "Qtd. env.", "Qtd. rec.", "Conf.", "No sist.", "Recebido por", "Ocorrência"].map(h =>
                      <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {caderno.linhas.filter(l => l.folha === f).map(l => (
                    <tr key={l.id} className="border-b border-slate-100" data-testid={`fl01-linha-${l.numero}`}>
                      <td className="px-2 py-1.5 font-bold">{l.linha_folha}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{fmtData(l.data)}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className={l.div ? "bg-red-50 text-red-700 border-red-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}>{l.tipo}</Badge></td>
                      <td className="px-2 py-1.5">{l.num_ref}</td>
                      <td className="px-2 py-1.5">{l.item}</td>
                      <td className="px-2 py-1.5">{l.unidade}</td>
                      <td className="px-2 py-1.5">{l.qtd_enviada}</td>
                      <td className="px-2 py-1.5">{l.qtd_recebida}</td>
                      <td className="px-2 py-1.5">{l.conf}</td>
                      <td className="px-2 py-1.5 text-center">{l.no_sistema ? "✓" : ""}</td>
                      <td className="px-2 py-1.5">{l.recebido_por}</td>
                      <td className="px-2 py-1.5 text-slate-500 max-w-56">{l.ocorrencia ? `${l.ocorrencia.o_que_aconteceu} ${l.ocorrencia.parou_equipe ? "· PAROU equipe" : ""}` : (l.div ? "pendente" : "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
      </div>
    </MainLayout>
  );
}
